// =============================================================================
// SHANIZ GAMING ADMIN - SUPPORT TICKETS LIST
// =============================================================================

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { supportService, SupportTicket, TicketStatus, TicketCategory, TicketPriority, userService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATUS_TABS: { key: TicketStatus | 'ALL'; label: string }[] = [
  { key: 'OPEN', label: 'Open' },
  { key: 'IN_PROGRESS', label: 'In Progress' },
  { key: 'ESCALATED', label: 'Escalated' },
  { key: 'RESOLVED', label: 'Resolved' },
  { key: 'ALL', label: 'All' },
];

const CATEGORIES: TicketCategory[] = ['BILLING', 'ACCOUNT', 'TECHNICAL', 'GAMEPLAY', 'KYC', 'OTHER'];

const PriorityChip: React.FC<{ p: SupportTicket['priority'] }> = ({ p }) => {
  const map: Record<SupportTicket['priority'], 'default' | 'info' | 'warning' | 'error'> = {
    LOW: 'default',
    NORMAL: 'info',
    HIGH: 'warning',
    URGENT: 'error',
  };
  return <Chip size="small" color={map[p] ?? 'default'} label={String(p ?? '—')} />;
};

const StatusChip: React.FC<{ s: TicketStatus }> = ({ s }) => {
  const map: Record<TicketStatus, 'info' | 'warning' | 'error' | 'success' | 'default'> = {
    OPEN: 'info',
    IN_PROGRESS: 'warning',
    ESCALATED: 'error',
    RESOLVED: 'success',
    CLOSED: 'default',
  };
  const label = String(s ?? '').replace('_', ' ');
  return <Chip size="small" color={map[s] ?? 'default'} label={label || '—'} />;
};

const Support: React.FC = () => {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user: currentAdmin } = useAuth();
  const [tab, setTab] = useState<TicketStatus | 'ALL'>('OPEN');
  const [category, setCategory] = useState<TicketCategory | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [snackbar, setSnackbar] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({ open: false, msg: '', sev: 'success' });

  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // ---- Create-ticket dialog state ----
  const [createOpen, setCreateOpen] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  const [userDebounced, setUserDebounced] = useState('');
  const [selectedUser, setSelectedUser] = useState<{ id: string; username?: string; email?: string } | null>(null);
  const [newTicket, setNewTicket] = useState<{
    subject: string;
    category: 'ACCOUNT' | 'PAYMENTS' | 'GAMES' | 'SECURITY' | 'KYC' | 'OTHER';
    priority: TicketPriority;
    initialMessage: string;
    assignToMe: boolean;
    assigneeId: string;
  }>({
    subject: '',
    category: 'ACCOUNT',
    priority: 'NORMAL',
    initialMessage: '',
    assignToMe: true,
    assigneeId: '',
  });

  React.useEffect(() => {
    const t = setTimeout(() => setUserDebounced(userQuery), 300);
    return () => clearTimeout(t);
  }, [userQuery]);

  const userSearch = useQuery({
    queryKey: ['support', 'user-search', userDebounced],
    queryFn: () => userService.search(userDebounced, 8),
    enabled: userDebounced.trim().length >= 2,
  });

  const adminsQuery = useQuery({
    queryKey: ['support', 'admins'],
    queryFn: () => supportService.listAdmins(),
    enabled: createOpen,
    retry: 0,
  });

  const createTicketMut = useMutation({
    mutationFn: (payload: Parameters<typeof supportService.create>[0]) => supportService.create(payload),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      setCreateOpen(false);
      setSelectedUser(null);
      setUserQuery('');
      setUserDebounced('');
      setNewTicket({
        subject: '',
        category: 'ACCOUNT',
        priority: 'NORMAL',
        initialMessage: '',
        assignToMe: true,
        assigneeId: '',
      });
      setSnackbar({ open: true, msg: 'Ticket created', sev: 'success' });
      // If the new ticket came back with an id, jump straight into it.
      if ((t as any)?.id) nav(`/support/${(t as any).id}`);
    },
    onError: (e: any) =>
      setSnackbar({ open: true, msg: e?.response?.data?.message || e?.message || 'Create failed', sev: 'error' }),
  });

  const createValid = Boolean(
    selectedUser?.id && newTicket.subject.trim().length >= 3 && newTicket.initialMessage.trim().length >= 3,
  );

  const handleCreateSubmit = () => {
    if (!selectedUser?.id || !createValid) return;
    const authorId = newTicket.assignToMe ? currentAdmin?.id : newTicket.assigneeId || undefined;
    createTicketMut.mutate({
      userId: selectedUser.id,
      subject: newTicket.subject.trim(),
      category: newTicket.category as any,
      priority: newTicket.priority,
      initialMessage: newTicket.initialMessage.trim(),
      authorId: authorId || undefined,
    });
  };

  const list = useQuery({
    queryKey: ['tickets', tab, category, debounced, page + 1, rowsPerPage],
    queryFn: () =>
      supportService.getAll({
        page: page + 1,
        limit: rowsPerPage,
        status: tab === 'ALL' ? undefined : tab,
        category: category === 'ALL' ? undefined : category,
        search: debounced || undefined,
      }),
  });

  const items = useMemo<SupportTicket[]>(
    () => (list.data?.items || list.data?.data || (list.data as unknown as { tickets?: SupportTicket[] })?.tickets || []) as SupportTicket[],
    [list.data]
  );
  const total = list.data?.pagination?.total ?? list.data?.total ?? items.length;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Support
        </Typography>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            placeholder="Search subject / user"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            sx={{ width: 280 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value as TicketCategory | 'ALL')}
            >
              <MenuItem value="ALL">All categories</MenuItem>
              {CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton onClick={() => list.refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': { borderColor: 'text.primary', bgcolor: 'action.hover' },
            }}
          >
            New ticket
          </Button>
        </Stack>
      </Box>

      <Card>
        <Tabs value={tab} onChange={(_, v) => { setTab(v); setPage(0); }} sx={{ borderBottom: '1px solid #262626' }}>
          {STATUS_TABS.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
        </Tabs>
        <CardContent>
          {list.isError && <Alert severity="error">Failed to load tickets.</Alert>}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Subject</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Assigned</TableCell>
                  <TableCell>Last activity</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {list.isLoading && Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 8 }).map((__, j) => (
                      <TableCell key={j}><Skeleton /></TableCell>
                    ))}
                  </TableRow>
                ))}
                {!list.isLoading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography color="text.secondary" py={3} textAlign="center">No tickets found</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {items.map((t) => (
                  <TableRow key={t.id} hover sx={{ cursor: 'pointer' }} onClick={() => nav(`/support/${t.id}`)}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{t.subject}</Typography>
                      <Typography variant="caption" color="text.secondary">#{String(t.id ?? '').slice(0, 8)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar src={t.user?.profilePicture || undefined} sx={{ width: 28, height: 28 }}>
                          {t.user?.username?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">{t.user?.username}</Typography>
                          <Typography variant="caption" color="text.secondary">{t.user?.email}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell><Chip size="small" label={t.category} /></TableCell>
                    <TableCell><PriorityChip p={t.priority} /></TableCell>
                    <TableCell><StatusChip s={t.status} /></TableCell>
                    <TableCell>
                      <Typography variant="body2">{t.assignedTo?.username || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{dayjs(t.lastMessageAt || t.updatedAt).format('MMM D HH:mm')}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); nav(`/support/${t.id}`); }}>
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total || 0}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </CardContent>
      </Card>

      {/* ---------- Create Support Ticket Dialog ---------- */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" fontWeight={700}>New support ticket</Typography>
          <Typography variant="body2" color="text.secondary">
            Opens a new thread on behalf of a user. The initial message is stored as an admin post.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3.5} sx={{ mt: 1 }}>
            {/* Section 1 — User */}
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={1}>
                1 · User
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small"
                    label="Search user"
                    placeholder="Start typing username…"
                    value={userQuery}
                    onChange={(e) => setUserQuery(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                  />
                  {userDebounced.trim().length >= 2 && !selectedUser && (
                    <Box sx={{ mt: 1, maxHeight: 220, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      {userSearch.isLoading && (
                        <Box sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">Searching…</Typography>
                        </Box>
                      )}
                      {!userSearch.isLoading && (userSearch.data || []).length === 0 && (
                        <Box sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">No matches</Typography>
                        </Box>
                      )}
                      {(userSearch.data || []).map((u: any) => (
                        <Box
                          key={u.id}
                          onClick={() => setSelectedUser({ id: u.id, username: u.username, email: u.email })}
                          sx={{ px: 1.5, py: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderBottom: 1, borderColor: 'divider' }}
                        >
                          <Typography variant="body2" fontWeight={600}>{u.username}</Typography>
                          <Typography variant="caption" color="text.secondary">{u.email || u.id}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Grid>
                {selectedUser && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, borderRadius: 1, bgcolor: 'action.hover', border: 1, borderColor: 'divider' }}>
                      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{selectedUser.username}</Typography>
                          <Typography variant="caption" color="text.secondary">{selectedUser.email || '—'}</Typography>
                        </Box>
                        <Button size="small" onClick={() => { setSelectedUser(null); setUserQuery(''); }}>
                          Change
                        </Button>
                      </Stack>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>

            <Divider />

            {/* Section 2 — Ticket details */}
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={1}>
                2 · Ticket details
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small"
                    label="Subject"
                    placeholder="Short, specific summary of the issue"
                    value={newTicket.subject}
                    onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                    required
                    error={newTicket.subject !== '' && newTicket.subject.trim().length < 3}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={newTicket.category}
                      label="Category"
                      onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value as any })}
                    >
                      <MenuItem value="ACCOUNT">Account</MenuItem>
                      <MenuItem value="PAYMENTS">Payments</MenuItem>
                      <MenuItem value="GAMES">Games</MenuItem>
                      <MenuItem value="SECURITY">Security</MenuItem>
                      <MenuItem value="KYC">KYC</MenuItem>
                      <MenuItem value="OTHER">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    color="primary"
                    value={newTicket.priority}
                    onChange={(_, v: TicketPriority | null) => v && setNewTicket({ ...newTicket, priority: v })}
                    fullWidth
                  >
                    <ToggleButton value="LOW">Low</ToggleButton>
                    <ToggleButton value="NORMAL">Normal</ToggleButton>
                    <ToggleButton value="HIGH">High</ToggleButton>
                    <ToggleButton value="URGENT">Urgent</ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Section 3 — Initial message */}
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={1}>
                3 · Initial message
              </Typography>
              <TextField
                fullWidth size="small" multiline minRows={4}
                placeholder="Explain the issue from the user's perspective; this will open the thread"
                value={newTicket.initialMessage}
                onChange={(e) => setNewTicket({ ...newTicket, initialMessage: e.target.value })}
                required
                sx={{ mt: 0.5 }}
                error={newTicket.initialMessage !== '' && newTicket.initialMessage.trim().length < 3}
              />
            </Box>

            <Divider />

            {/* Section 4 — Assignment */}
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={1}>
                4 · Assignment (optional)
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newTicket.assignToMe}
                        onChange={(e) => setNewTicket({ ...newTicket, assignToMe: e.target.checked })}
                      />
                    }
                    label={`Assign to me${currentAdmin?.name ? ` (${currentAdmin.name})` : ''}`}
                  />
                </Grid>
                {!newTicket.assignToMe && (
                  <Grid item xs={12} sm={8}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Assignee</InputLabel>
                      <Select
                        value={newTicket.assigneeId}
                        label="Assignee"
                        onChange={(e) => setNewTicket({ ...newTicket, assigneeId: String(e.target.value) })}
                      >
                        <MenuItem value="">Unassigned</MenuItem>
                        {(adminsQuery.data || []).map((a) => (
                          <MenuItem key={a.id} value={a.id}>
                            {a.username} · {a.role}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </Box>

            {/* Summary card */}
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', border: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={1} sx={{ display: 'block', mb: 1 }}>
                SUMMARY
              </Typography>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                <Box>
                  <Typography variant="caption" color="text.secondary">User</Typography>
                  <Typography fontWeight={700}>{selectedUser?.username || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Subject</Typography>
                  <Typography fontWeight={700}>{newTicket.subject || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Category / Priority</Typography>
                  <Typography fontWeight={700}>{newTicket.category} · {newTicket.priority}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Author</Typography>
                  <Typography fontWeight={700}>
                    {newTicket.assignToMe ? (currentAdmin?.name || 'current admin') : (newTicket.assigneeId || 'system')}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ color: 'text.secondary' }}>Cancel</Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCreateSubmit}
            disabled={!createValid || createTicketMut.isPending}
            sx={{
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': { borderColor: 'text.primary', bgcolor: 'action.hover' },
              px: 3,
            }}
          >
            {createTicketMut.isPending ? 'Creating…' : 'Create ticket'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.sev} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Support;
